import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export const collapsedHeadingsKey = new PluginKey('collapsedHeadings');

export const COLLAPSE_LEVELS = new Set([1, 2]);

const isHeading = node => node?.type?.name === 'heading';

/** Collect `"<level>:<text>"` keys for h1/h2 headings in a ProseMirror doc. */
export const collectCollapsibleHeadingKeys = doc => {
  const keys = [];
  doc.descendants(node => {
    if (!isHeading(node)) return false;
    const level = node.attrs.level || 1;
    if (!COLLAPSE_LEVELS.has(level)) return false;
    keys.push(`${level}:${node.textContent}`);
    return false;
  });
  return keys;
};

const buildDecorations = (doc, collapsedKeys) => {
  const collapsed = collapsedKeys || new Set();
  const decorations = [];
  let hideUntilLevel = null;

  doc.descendants((node, pos) => {
    if (!node.isBlock) return true;
    if (isHeading(node)) {
      const level = node.attrs.level || 1;
      if (hideUntilLevel !== null && level <= hideUntilLevel) {
        hideUntilLevel = null;
      }
      const insideCollapsedParent = hideUntilLevel !== null;
      const key = `${level}:${node.textContent}`;
      const isCollapsed = collapsed.has(key);
      const classes = ['tiptap-heading', `tiptap-heading-l${level}`];
      if (isCollapsed) classes.push('tiptap-heading-collapsed');
      if (insideCollapsedParent) classes.push('tiptap-collapsed-block');
      decorations.push(
        Decoration.node(pos, pos + node.nodeSize, {
          class: classes.join(' '),
          'data-heading-key': key,
          'data-collapsed': isCollapsed ? 'true' : 'false',
        }),
      );
      if ((isCollapsed || insideCollapsedParent) && COLLAPSE_LEVELS.has(level)) {
        hideUntilLevel = insideCollapsedParent ? hideUntilLevel : level;
      }
      return false;
    }
    if (hideUntilLevel !== null) {
      decorations.push(
        Decoration.node(pos, pos + node.nodeSize, {
          class: 'tiptap-collapsed-block',
        }),
      );
    }
    return false;
  });

  return DecorationSet.create(doc, decorations);
};

/**
 * Tiptap extension that lets the host component maintain a Set of collapsed heading keys
 * (`"<level>:<text>"`) and decorate the document so that blocks following a collapsed
 * heading are hidden until the next heading of equal or higher level.
 */
const CollapsibleHeadings = Extension.create({
  name: 'collapsibleHeadings',

  addOptions() {
    return {
      getCollapsedKeys: () => new Set(),
    };
  },

  addKeyboardShortcuts() {
    const { options } = this;
    return {
      Enter: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        if (!selection.empty) return false;
        const { $from } = selection;
        const { parent } = $from;
        if (parent.type.name !== 'heading') return false;
        if ($from.parentOffset !== parent.content.size) return false;

        const level = parent.attrs.level || 1;
        const key = `${level}:${parent.textContent}`;
        const collapsedKeys = options.getCollapsedKeys();
        if (!collapsedKeys.has(key)) return false;

        const headingStart = $from.before($from.depth);

        let endPos = state.doc.content.size;
        state.doc.forEach((node, offset) => {
          if (offset <= headingStart) return;
          if (endPos !== state.doc.content.size) return;
          if (node.type.name === 'heading' && (node.attrs.level || 1) <= level) {
            endPos = offset;
          }
        });

        const headingType = state.schema.nodes.heading;
        if (!headingType) return false;
        const newHeading = headingType.create({ level });
        let tr = state.tr.insert(endPos, newHeading);
        tr = tr.setSelection(TextSelection.create(tr.doc, endPos + 1));
        tr = tr.scrollIntoView();
        editor.view.dispatch(tr);
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    const { options } = this;
    return [
      new Plugin({
        key: collapsedHeadingsKey,
        state: {
          init(_, state) {
            return buildDecorations(state.doc, options.getCollapsedKeys());
          },
          apply(tr, value, _oldState, newState) {
            const meta = tr.getMeta(collapsedHeadingsKey);
            if (meta || tr.docChanged) {
              return buildDecorations(newState.doc, options.getCollapsedKeys());
            }
            return value.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
          handleClickOn(view, pos, node, _nodePos, event) {
            if (!isHeading(node)) return false;
            const level = node.attrs.level || 1;
            if (!COLLAPSE_LEVELS.has(level)) return false;
            const { target } = event;
            if (!(target instanceof HTMLElement)) return false;
            const heading = target.closest('.tiptap-heading');
            if (!heading) return false;
            // Toggle when the click falls in the left gutter where the chevron
            // sits (between -28px and 0px relative to the heading's left edge).
            const rect = heading.getBoundingClientRect();
            const x = event.clientX - rect.left;
            if (x > 0 || x < -28) return false;
            event.preventDefault();
            const key = `${level}:${node.textContent}`;
            options.onToggle?.(key);
            return true;
          },
        },
      }),
    ];
  },
});

export default CollapsibleHeadings;
